import { useState, useRef, useEffect } from "react";
import FileNotificationBar from "./Notification/FileNotificationBar";
import NotificationBell from "./Notification/NotificationBell";
import { NotificationsContextType } from "@utils/useNotifications";
import { useNavigate } from "react-router-dom";
import { GoSignOut } from "react-icons/go";
import { useUser } from "@services/UserContext";
import LogoutModal from "./LogoutModal";
import { useUserService } from "@utils/useUsers";
import { toastService } from "@services/ToastService";

interface TitleHeaderProps {
  title: string;
  titleStyle?: string;
  subtitle?: string;
  subtitleStyle?: string;
  notificationStyle?: string;
  children?: React.ReactNode;
  notificationsContext: NotificationsContextType;
}

const TitleHeader: React.FC<TitleHeaderProps> = ({
  title,
  titleStyle,
  subtitle,
  subtitleStyle,
  notificationStyle,
  children,
  notificationsContext,
}) => {
  const navigate = useNavigate();
  const { logout } = useUserService();

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      toastService.error("Failed to logout. Please try again.");
      // log the error for debugging
      console.error("Failed to logout:", error);
    }
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { user } = useUser();

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navigateToHome = () => {
    navigate("/assistant");
  };

  return (
    <>
      <div className="px-4 bg-[#F2F4F7]">
        <div
          className={`flex justify-between items-center mb-4 mt-4 min-h-[2.475rem] max-h-[2.475rem] 2xl:min-h-[2.75rem] 2xl:max-h-[2.75rem]  ${titleStyle}`}
        >
          <div className="ml-8 cursor-pointer" onClick={navigateToHome}>
            <img alt="THE ARK" src="/logo_new.svg" className={"w-32"} />
          </div>

          <div className="flex items-center">
            <div
              className={`flex space-x-4 mr-8 items-center ${notificationStyle}`}
            >
              <FileNotificationBar />
              <NotificationBell notificationsContext={notificationsContext} />
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center justify-between space-x-7 pr-3 rounded-md pl-2 py-[0.3rem] bg-white hover:bg-gray-100 border border-gray-200"
                >
                  {/* <div className="flex items-center space-x-2"> */}
                  <span className=" font-bold text-sm bg-blue-600 text-white flex items-center justify-center rounded-full w-8 h-8">
                    {user && user.first_name && user.last_name
                      ? user.first_name[0] + user.last_name[0]
                      : "U"}
                  </span>
                  <span className="text-sm font-medium text-slate-800">
                    {user && user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : "User Name"}
                  </span>
                  {/* </div> */}
                  <svg
                    className="w-4 h-4 text-slate-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-[#1e40af] text-white shadow-xl border border-gray-200 rounded-xl pt-4 overflow-hidden z-50">
                    <div className="mb-4 px-4">
                      <div className="text-sm font-semibold">
                        {user && user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : "User Name"}
                      </div>
                      <div className="text-xs opacity-80">
                        {user && user.email}
                      </div>
                      <div className="text-xs opacity-80">
                        {user ? user.company_relation.company.name : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => setIsLogoutModalOpen(true)}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-[#1e40af] bg-white rounded-b  hover:bg-blue-50"
                    >
                      <span className="text-sm font-semibold">Sign out</span>
                      <GoSignOut />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <hr className="ml-[-3rem] mr-[-3rem]" />
      </div>

      <LogoutModal
        onProceed={handleLogout}
        onCancel={() => setIsLogoutModalOpen(false)}
        isOpen={isLogoutModalOpen}
      />
    </>
  );
};

export default TitleHeader;
